import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  width: "0",
  height: "0",
  "aria-hidden": "true",
  class: "k-liquid-glass__svg"
}
const _hoisted_2 = ["width", "height"]
const _hoisted_3 = ["href", "width", "height"]
const _hoisted_4 = ["scale", "result"]
const _hoisted_5 = ["in", "values", "result"]

import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useId } from 'vue'

interface IProps {
  /**
   * Laid over the refracted backdrop, to suit what's under the glass and on it (e.g. a dark tint under light content);
   * none by default. Any CSS colour but a var(); an rgba() one also sets how much of the backdrop shows through. Where
   * the backdrop is refracted, it thins out towards the edge, where the glass refracts most, so the refraction shows.
   */
  tint?: string
}

/** The element's border box, in px */
interface IGlassBox {
  width: number
  height: number
  radius: number
}

/** What's drawn for the element's box and tint */
interface IGlassMaps {
  /** The element's size, in px, which both images cover */
  width: number
  height: number
  /** The displacement map's scale: twice the furthest it shifts the backdrop, in px */
  scale: number
  /**
   * Data URLs. The displacement map shifts the backdrop by its red (x) and green (y), from the 50% of no shift; the
   * surface is the tint, with the light that catches the glass over it.
   */
  displacement: string
  surface: string
}

/** Where a point lies against the rounded rectangle's edge */
interface IEdgePoint {
  /** How far inside the edge it is, in px; negative outside it */
  inset: number
  /** The edge's outward normal there */
  normalX: number
  normalY: number
}

const BEZEL_SHARE = 0.5
const MAX_BEZEL = 32
// How far the bezel shifts the backdrop at the very edge, as a share of its width
const MAX_SHIFT_SHARE = 0.9
// Glass's index of refraction
const IOR = 1.5
// The refraction at the very edge, where the bezel's surface is vertical, which the rest is relative to
const ABERRATION = 0.08
const RIM_WIDTH = 1.5
const SHEEN = 0.35
// Applied to the backdrop after the refraction
const BACKDROP_EFFECTS = 'blur(1px) brightness(1.1) saturate(1.5)'

export default /*@__PURE__*/_defineComponent({
  ...{ name: 'KLiquidGlass' },
  __name: 'KLiquidGlass',
  props: {
    tint: { default: 'transparent' }
  },
  setup(__props: any) {


const props = __props

// WebKit, which every browser on iOS runs on, parses an SVG filter in backdrop-filter but can't draw one, so it drops
// the whole backdrop-filter (WebKit bug 245510). There the backdrop is only blurred and coloured, not refracted. It's
// told by the engine, as CSS.supports() only checks the parse: navigator.vendor is Apple's in WebKit, whatever the
// browser or user agent.
const CAN_REFRACT = !navigator.vendor.startsWith('Apple')
// The bezel is the curved band along the edge that refracts; inside it the glass is flat. It's this share of the
// shorter side wide, up to MAX_BEZEL px.
const EDGE_REFRACTION = Math.tan(Math.PI / 2 - Math.asin(1 / IOR))
// Each channel's share of the refraction, as a prism splits light: red is shifted most and blue least
const CHANNELS = [
  { name: 'red', refraction: 1, matrix: '1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0' },
  { name: 'green', refraction: 1 - ABERRATION, matrix: '0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0' },
  { name: 'blue', refraction: 1 - 2 * ABERRATION, matrix: '0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0' },
]
// The tint's share at the very edge, from its full strength where the glass doesn't refract; even where nothing is
const EDGE_TINT = CAN_REFRACT ? 0.3 : 1
// The light comes from the top left. It catches the rim, a thin line that fades out this many px in from the edge,
// brightest where the edge faces it or faces away (where it reflects off the inside), and the bezel on its side,
// as a soft sheen up to this opacity.
const LIGHT_X = -Math.SQRT1_2
const LIGHT_Y = -Math.SQRT1_2
const EMPTY_MAPS: IGlassMaps = { width: 0, height: 0, scale: 0, displacement: '', surface: '' }

// Each instance has its own filter, drawn for its size
const filterId = `k-liquid-glass-${useId()}`
const backdropFilter = CAN_REFRACT ? `url(#${filterId}) ${BACKDROP_EFFECTS}` : BACKDROP_EFFECTS
const rootRef = ref<HTMLDivElement | null>(null)
const box = shallowRef<IGlassBox | null>(null)

const maps = computed(() => (box.value ? drawMaps(box.value, props.tint) : EMPTY_MAPS))
// Until the element is measured, the tint is laid evenly and the backdrop isn't refracted, as a filter with an empty
// map would shift the whole backdrop
const rootStyle = computed(() => {
  const { surface } = maps.value
  return surface
    ? { backgroundImage: `url(${surface})`, backdropFilter }
    : { backgroundColor: props.tint, backdropFilter: BACKDROP_EFFECTS }
})

// The refraction t of the way in across the bezel, from 1 at the edge to 0 where it meets the flat top. A ray
// straight down meets the bezel's surface, a convex quarter circle, at an angle of asin(1 - t) from its normal there,
// and bends by Snell's law.
function getRefraction(t: number) {
  const incidence = Math.asin(1 - t)
  return Math.tan(incidence - Math.asin(Math.sin(incidence) / IOR)) / EDGE_REFRACTION
}

// Measured from the rectangle's centre, whose corners are rounded by `radius`
function getEdgePoint(x: number, y: number, halfWidth: number, halfHeight: number, radius: number): IEdgePoint {
  const offsetX = Math.abs(x) - (halfWidth - radius)
  const offsetY = Math.abs(y) - (halfHeight - radius)
  const signX = x < 0 ? -1 : 1
  const signY = y < 0 ? -1 : 1
  // In a corner, the edge curves around the corner's centre
  if (offsetX > 0 && offsetY > 0) {
    const distance = Math.hypot(offsetX, offsetY)
    return { inset: radius - distance, normalX: (offsetX / distance) * signX, normalY: (offsetY / distance) * signY }
  }
  // Elsewhere, the nearer side
  return offsetX > offsetY
    ? { inset: radius - offsetX, normalX: signX, normalY: 0 }
    : { inset: radius - offsetY, normalX: 0, normalY: signY }
}

// As [red, green, blue, alpha], each 0–255; transparent if the canvas can't read it
function parseColor(color: string) {
  const context = document.createElement('canvas').getContext('2d')
  if (!context) return [0, 0, 0, 0]

  context.fillStyle = 'transparent'
  context.fillStyle = color
  context.fillRect(0, 0, 1, 1)
  return [...context.getImageData(0, 0, 1, 1).data]
}

function toDataUrl(image: ImageData) {
  const canvas = document.createElement('canvas')
  canvas.width = image.width
  canvas.height = image.height
  canvas.getContext('2d')?.putImageData(image, 0, 0)
  return canvas.toDataURL()
}

// Drawn at the screen's resolution (up to 2x), so the rim stays sharp
function drawMaps({ width, height, radius }: IGlassBox, tint: string): IGlassMaps {
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
  const columns = Math.max(Math.round(width * pixelRatio), 1)
  const rows = Math.max(Math.round(height * pixelRatio), 1)
  const bezel = Math.min(Math.min(width, height) * BEZEL_SHARE, MAX_BEZEL)
  const corner = Math.min(radius, width / 2, height / 2)
  const [tintRed, tintGreen, tintBlue, tintAlpha] = parseColor(tint)
  const displacement = new ImageData(columns, rows)
  const surface = new ImageData(columns, rows)

  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      // The pixel's centre, in px from the element's
      const x = (column + 0.5) / pixelRatio - width / 2
      const y = (row + 0.5) / pixelRatio - height / 2
      const { inset, normalX, normalY } = getEdgePoint(x, y, width / 2, height / 2, corner)
      const refraction = inset < bezel ? getRefraction(Math.max(inset, 0) / bezel) : 0
      const index = (row * columns + column) * 4

      // Inwards: a convex surface bends the rays towards its middle, so the edge shows what's further in, magnified
      displacement.data[index] = 128 - normalX * refraction * 127
      displacement.data[index + 1] = 128 - normalY * refraction * 127
      displacement.data[index + 2] = 128
      displacement.data[index + 3] = 255

      // The light, in white, over the tint
      const facing = normalX * LIGHT_X + normalY * LIGHT_Y
      const rim = inset < RIM_WIDTH ? (1 - Math.max(inset, 0) / RIM_WIDTH) ** 1.5 * (0.4 + 0.6 * facing ** 2) : 0
      const sheen = inset < bezel ? SHEEN * (1 - Math.max(inset, 0) / bezel) ** 2 * Math.max(facing, 0) ** 2 : 0
      const light = Math.min(rim + sheen, 1)
      const tintOpacity = (tintAlpha / 255) * (1 - (1 - EDGE_TINT) * refraction) * (1 - light)
      const opacity = light + tintOpacity
      if (opacity) {
        surface.data[index] = (255 * light + tintRed * tintOpacity) / opacity
        surface.data[index + 1] = (255 * light + tintGreen * tintOpacity) / opacity
        surface.data[index + 2] = (255 * light + tintBlue * tintOpacity) / opacity
        surface.data[index + 3] = 255 * opacity
      }
    }
  }

  return {
    width,
    height,
    scale: 2 * bezel * MAX_SHIFT_SHARE,
    displacement: toDataUrl(displacement),
    surface: toDataUrl(surface),
  }
}

// Measured whenever the element resizes, with its corner radius then (read in px)
const resizeObserver = new ResizeObserver(([entry]) => {
  const [{ inlineSize, blockSize }] = entry.borderBoxSize
  if (!inlineSize || !blockSize) return

  const radius = parseFloat(getComputedStyle(entry.target).borderTopLeftRadius) || 0
  box.value = { width: inlineSize, height: blockSize, radius }
})

onMounted(() => {
  if (rootRef.value) resizeObserver.observe(rootRef.value)
})

onBeforeUnmount(() => resizeObserver.disconnect())

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: "k-liquid-glass",
    style: _normalizeStyle(rootStyle.value)
  }, [
    (_openBlock(), _createElementBlock("svg", _hoisted_1, [
      _createElementVNode("defs", null, [
        _createElementVNode("filter", {
          id: filterId,
          filterUnits: "userSpaceOnUse",
          "color-interpolation-filters": "sRGB",
          x: "0",
          y: "0",
          width: maps.value.width,
          height: maps.value.height
        }, [
          _createElementVNode("feImage", {
            href: maps.value.displacement,
            width: maps.value.width,
            height: maps.value.height,
            preserveAspectRatio: "none",
            result: "map"
          }, null, 8, _hoisted_3),
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(CHANNELS, (channel) => {
            return (_openBlock(), _createElementBlock(_Fragment, {
              key: channel.name
            }, [
              _createElementVNode("feDisplacementMap", {
                in: "SourceGraphic",
                in2: "map",
                xChannelSelector: "R",
                yChannelSelector: "G",
                scale: maps.value.scale * channel.refraction,
                result: `${channel.name}-refracted`
              }, null, 8, _hoisted_4),
              _createElementVNode("feColorMatrix", {
                in: `${channel.name}-refracted`,
                type: "matrix",
                values: channel.matrix,
                result: channel.name
              }, null, 8, _hoisted_5)
            ], 64))
          }), 64)),
          _cache[0] || (_cache[0] = _createElementVNode("feBlend", {
            in: "red",
            in2: "green",
            mode: "screen",
            result: "red-green"
          }, null, -1)),
          _cache[1] || (_cache[1] = _createElementVNode("feBlend", {
            in: "red-green",
            in2: "blue",
            mode: "screen"
          }, null, -1))
        ], 8, _hoisted_2)
      ])
    ])),
    _renderSlot(_ctx.$slots, "default")
  ], 4))
}
}

})